import { FilesUploader, If, VideoThumbnailSelector } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import {
  generateVideoThumbnails,
  VideoThumbnail
} from "helpers/generateVideoThumbnails";
import { useImagesWatermarks } from "hooks";
import { useDirectUploadToS3 } from "libs/directUpload";
import { useState, useEffect } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FormField, MultipleMediaFilesPicker } from "ui";

import { ProductVideoVariant } from "../../../../../constants/enums";
import { ProductFormValues } from "../../ProductDetailsForm.types";
import { VariantBlockPartProps } from "./VariantBlock.types";

import styles from "../../ProductDetailsForm.module.scss";

const VARIANT_VIDEO_SIZE = {
  width: 176,
  height: 248
};

export const VariantImagesBlock = ({ variantIndex }: VariantBlockPartProps) => {
  const { t } = useTranslation();
  const { control, getValues, watch, setValue } =
    useFormContext<ProductFormValues>();
  const { uploader } = useDirectUploadToS3();

  const { addWatermarkToImages, processingImagesCount } = useImagesWatermarks();

  const {
    fields: mediaFiles,
    append: appendMediaFile,
    move: moveMediaFile,
    remove: removeMediaFile
  } = useFieldArray({
    control,
    name: `variants.${variantIndex}.images`
  });

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnails, setThumbnails] = useState<VideoThumbnail[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const productVideoVariant = watch("productVideoVariant");
  const variantImages = watch(`variants.${variantIndex}.images`);

  const isForEachVideoVariant =
    productVideoVariant === ProductVideoVariant.FOR_EACH_VIDEO;

  const hasVideo =
    isForEachVideoVariant &&
    variantImages?.some((img) => img.mediaType === MediaType.VIDEO);

  useEffect(() => {
    if (!videoFile) {
      setThumbnails([]);
      setSelectedIndex(0);
      return;
    }

    const generate = async () => {
      setIsGenerating(true);
      try {
        const generated = await generateVideoThumbnails(
          videoFile,
          VARIANT_VIDEO_SIZE
        );
        setThumbnails(generated);
        setSelectedIndex(0);

        if (generated.length > 0) {
          await uploadThumbnail(generated[0]);
        }
      } catch {
        toast.error(t("Failed to generate thumbnails"));
      } finally {
        setIsGenerating(false);
      }
    };

    generate();
  }, [videoFile]);

  const uploadThumbnail = async (thumbnail: VideoThumbnail) => {
    setIsUploading(true);
    try {
      const file = new File([thumbnail.blob], "thumbnail.jpg", {
        type: "image/jpeg"
      });

      const fileWithMetadata = Object.assign(file, {
        width: VARIANT_VIDEO_SIZE.width,
        height: VARIANT_VIDEO_SIZE.height
      });

      const media = await uploader(fileWithMetadata, {
        directoryKey: MediaDirectoryKeys.ProductVariant,
        onError: () => {
          toast.error(t("Failed to upload thumbnail"));
        }
      });

      if (media) {
        setValue(`variants.${variantIndex}.videoThumbnail`, media);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectThumbnail = async (newIndex: number) => {
    if (newIndex === selectedIndex || !thumbnails[newIndex]) return;

    setSelectedIndex(newIndex);
    await uploadThumbnail(thumbnails[newIndex]);
  };

  const handleVideoFileReceived = (file: File) => {
    setVideoFile(file);
  };

  const handleRemoveMediaFile = (index: number) => {
    const removedFile = mediaFiles[index];
    if (removedFile && removedFile.mediaType === MediaType.VIDEO) {
      setVideoFile(null);
      setThumbnails([]);
      setSelectedIndex(0);
      setValue(`variants.${variantIndex}.videoThumbnail`, null);
    }
    removeMediaFile(index);
  };

  return (
    <div className={styles.productForm__imagesContainer}>
      <Controller
        control={control}
        name={`variants.${variantIndex}.images`}
        render={({ fieldState: { error } }) => (
          <FormField
            label={`Product Media Files (in this color)`}
            errorMessage={error?.message}
          >
            <FilesUploader
              onSuccessUploading={appendMediaFile}
              uploadDirectoryKey={MediaDirectoryKeys.ProductVariant}
              onVideoFileReceived={
                isForEachVideoVariant ? handleVideoFileReceived : undefined
              }
            >
              {(uploaderProps) => (
                <MultipleMediaFilesPicker
                  {...uploaderProps}
                  onFilesDrop={async (files) => {
                    const watermarkedFiles = await addWatermarkToImages(
                      files,
                      getValues(`variants.${variantIndex}.shortId`)
                    );

                    uploaderProps.onFilesDrop(watermarkedFiles);
                  }}
                  loadingFilesCounter={
                    uploaderProps.loadingFilesCounter || processingImagesCount
                  }
                  allowedMediaTypes={
                    isForEachVideoVariant
                      ? [MediaType.IMAGE, MediaType.VIDEO]
                      : [MediaType.IMAGE]
                  }
                  value={mediaFiles}
                  filesLimit={12}
                  label={"176:248"}
                  onDeleteMediaFile={handleRemoveMediaFile}
                  onReorder={moveMediaFile}
                />
              )}
            </FilesUploader>

            <If condition={hasVideo}>
              <VideoThumbnailSelector
                thumbnails={thumbnails}
                selectedIndex={selectedIndex}
                onSelect={handleSelectThumbnail}
                isLoading={isGenerating || isUploading}
                aspectRatio={
                  VARIANT_VIDEO_SIZE.width / VARIANT_VIDEO_SIZE.height
                }
              />
            </If>
          </FormField>
        )}
      />
    </div>
  );
};
